import { Fragment, useMemo } from "react";
import { AttachmentImage } from "@/components/ui/attachment-media";
import type {
  CardPreviewDensity,
  CardPreviewProps,
} from "@/components/shapes/card-previews";
import {
  PreviewBody,
  PreviewCheckRow,
  PreviewTitle,
} from "@/components/shapes/shared/card-preview-parts";
import { SurfaceFade } from "@/components/ui";
import { cn } from "@/lib/cn";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import {
  docCardLines,
  docCoverImage,
  type DocCardLine,
} from "./doc-card-lines";

/**
 * Where the lines stop and fade: a live preview, clipped, not a line
 * count. A regular card grows with its document up to 17em of lines and
 * fades the last 40px only of text that reaches the cap, so a short
 * document shows whole. A compact card shows what its box holds and fades
 * at the box's foot.
 */
const FLOW_CLASS: Record<CardPreviewDensity, string> = {
  regular: "max-h-[17em]",
  compact: "flex-1",
};
const FLOW_CUT: Record<CardPreviewDensity, string | undefined> = {
  regular: "17em",
  compact: undefined,
};

/** Consecutive bullets as one list; every other line on its own. */
type DocCardBlock =
  | { kind: "line"; line: DocCardLine }
  | { kind: "list"; items: DocCardLine[] };

function docCardBlocks(lines: readonly DocCardLine[]): DocCardBlock[] {
  const blocks: DocCardBlock[] = [];
  for (const line of lines) {
    const previous = blocks[blocks.length - 1];
    if (line.kind === "bullet") {
      if (previous?.kind === "list") previous.items.push(line);
      else blocks.push({ kind: "list", items: [line] });
      continue;
    }
    blocks.push({ kind: "line", line });
  }
  return blocks;
}

/** A line's text with its links drawn as links (`MarkdownContent`'s recipe). */
function DocCardText({ line }: { line: DocCardLine }) {
  if (!line.links) return line.text;
  let at = 0;
  return (
    <>
      {line.links.map((link) => {
        const before = line.text.slice(at, link.start);
        at = link.end;
        return (
          <Fragment key={link.start}>
            {before}
            <span
              className="text-accent underline underline-offset-2"
              data-testid="doc-card-link"
            >
              {line.text.slice(link.start, link.end)}
            </span>
          </Fragment>
        );
      })}
      {line.text.slice(at)}
    </>
  );
}

function DocCardLineView({ line }: { line: DocCardLine }) {
  switch (line.kind) {
    case "heading":
      return (
        <p className="mb-[0.1em] mt-[1em] font-display text-[1.08em] font-medium text-card-ink first:mt-0">
          <DocCardText line={line} />
        </p>
      );
    case "check":
      return (
        <PreviewCheckRow done={line.done === true}>
          <DocCardText line={line} />
        </PreviewCheckRow>
      );
    case "quote":
      return (
        <p className="whitespace-pre-line pl-[0.9em] italic">
          <DocCardText line={line} />
        </p>
      );
    case "code":
      return (
        <p className="overflow-hidden whitespace-pre font-mono text-[0.85em]">
          {line.text}
        </p>
      );
    default:
      return (
        <p className="whitespace-pre-line">
          <DocCardText line={line} />
        </p>
      );
  }
}

/**
 * Docs and markdown frames (mockup `.doc`): a small version of the
 * document. The title large in Satoshi, then paragraphs, lists with quiet
 * markers, headings, checks, quotes and code in the Doc's
 * order; the card clips the rest with a fade.
 */
export function DocCardPreview({ frame, source, density }: CardPreviewProps) {
  const blocks = useMemo(
    () => docCardBlocks(docCardLines(frame.frameType, source)),
    [frame.frameType, source],
  );
  const cover = useMemo(
    () => docCoverImage(frame.frameType, source),
    [frame.frameType, source],
  );
  const body = (
    <PreviewBody density={density} className="gap-0">
      <PreviewTitle>{frameDisplayTitle(frame)}</PreviewTitle>
      {blocks.length > 0 && (
        <div
          className={cn(
            "relative flex min-h-0 flex-col gap-[0.8em] overflow-hidden leading-[1.6] text-card-body",
            FLOW_CLASS[density],
          )}
          data-testid="doc-card-lines"
        >
          {blocks.map((block, index) =>
            block.kind === "list" ? (
              <ul
                key={index}
                className="list-disc pl-[1.1em] leading-[1.65] marker:text-card-muted"
              >
                {block.items.map((item, itemIndex) => (
                  <li key={itemIndex}>
                    <DocCardText line={item} />
                  </li>
                ))}
              </ul>
            ) : (
              <DocCardLineView key={index} line={block.line} />
            ),
          )}
          <SurfaceFade at={FLOW_CUT[density]} />
        </div>
      )}
    </PreviewBody>
  );
  if (!cover) return body;
  // A cover image sits edge to edge above the paper, as in the mockup.
  return (
    <div
      className={cn("flex min-h-0 flex-col", density === "compact" && "h-full")}
    >
      <AttachmentImage
        src={cover.url}
        alt={cover.alt}
        className={cn(
          "block w-full shrink-0 object-cover",
          density === "compact" ? "h-1/2" : "aspect-[16/9]",
        )}
        data-testid="doc-card-cover"
        draggable={false}
        loading="lazy"
      />
      {body}
    </div>
  );
}
